import React, { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Cookie-based admin session ────────────────────────────────────────
// The admin password is validated server-side and exchanged for an httpOnly
// signed JWT cookie (`xaluca_admin_session`). The frontend never stores
// the password — every admin call includes `credentials: "include"` so
// the cookie travels with the request.
//
// Legacy helper compatibility:
//   • `adminHeaders()` — now returns `{}` (no-op). Existing call sites that
//     spread it into fetch headers keep working; the auth is carried by the
//     cookie instead of a header.
//   • `clearAdminPassword()` — now POSTs to /api/admin/session/logout.
//   • `adminFetch(url, opts)` — thin wrapper that always sends credentials.
export const adminHeaders = () => ({});

export const adminFetch = (url, opts = {}) =>
  fetch(url, { credentials: "include", ...opts });

export const clearAdminPassword = () =>
  adminFetch(`${API}/api/admin/session/logout`, { method: "POST" }).catch(() => null);

const PasswordForm = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    // Trim — paste/autofill/mobile keyboards often add a trailing space.
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setError("Please enter the access code.");
      setBusy(false);
      return;
    }
    // Auto-retry transient gateway errors after a fresh deploy.
    const probe = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await fetch(`${API}/api/admin/session/login`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ password: trimmed }),
          });
          if ([502, 503, 504].includes(res.status) && attempt < 2) {
            await new Promise((r) => setTimeout(r, 1200 + attempt * 600));
            continue;
          }
          return res;
        } catch (err) {
          if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 1200 + attempt * 600));
            continue;
          }
          throw err;
        }
      }
      return null;
    };
    try {
      const res = await probe();
      if (!res) return;
      if (res.status === 401) {
        setError("Incorrect access code.");
        setBusy(false);
        return;
      }
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || "Too many attempts. Try again in a few minutes.");
        setBusy(false);
        return;
      }
      if (!res.ok) {
        setError(
          res.status === 404
            ? "Admin endpoints not found on this server. Please redeploy the backend."
            : `Server is temporarily unavailable (HTTP ${res.status}). Please retry in a moment.`
        );
        setBusy(false);
        return;
      }
      // Success — server has set the httpOnly cookie. No client-side storage.
      onUnlock();
    } catch (err) {
      setError(err.message || "Network error");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-auth-gate">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[#2C2621]/10 p-8 md:p-10"
        data-testid="admin-auth-form"
      >
        <p className="overline text-[#C16542]">Grup Xaluca · Admin</p>
        <h1 className="font-serif-x text-3xl md:text-4xl mt-3 tracking-tight">
          Enter the access code
        </h1>
        <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">
          One code for the whole admin area — no accounts.
        </p>
        <label className="block mt-7 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
          Access code
        </label>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          className="mt-2 w-full border border-[#2C2621]/15 px-4 py-3.5 text-base focus:outline-none focus:border-[#C16542]"
          data-testid="admin-auth-input"
        />
        {error && (
          <p className="mt-3 text-sm text-[#C16542]" data-testid="admin-auth-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !value}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 text-[#FDFBF7] px-6 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
          data-testid="admin-auth-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Unlock
        </button>
      </form>
    </div>
  );
};

const AdminAuthGate = ({ children }) => {
  // null = still checking, false = not authed, true = authed
  const [authState, setAuthState] = useState(null);

  const verify = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/session/verify`, {
        method: "GET",
        credentials: "include",
      });
      setAuthState(res.ok);
    } catch (err) {
      setAuthState(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  const logout = useCallback(async () => {
    await clearAdminPassword();
    setAuthState(false);
  }, []);

  if (authState === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#5C5248]" data-testid="admin-auth-checking">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking access…
      </div>
    );
  }
  if (authState === false) {
    return <PasswordForm onUnlock={() => setAuthState(true)} />;
  }
  return (
    <>
      {children}
      <button
        type="button"
        onClick={logout}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-[#2C2621] hover:bg-[#1A1513] text-[#FDFBF7] px-4 py-2 text-[10px] tracking-[0.25em] uppercase rounded-full shadow-lg transition-colors"
        data-testid="admin-logout"
        aria-label="Log out of admin"
      >
        <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
        Log out
      </button>
    </>
  );
};

export default AdminAuthGate;
